import { PERMS, hasPermission } from '../../lib/permissions.js';
/**
 * Capability detail — the sources that can serve one capability, each with a
 * one-click on/off toggle. Routed at `/capabilities/:capability`. Turning a
 * source on when another is already active opens a swap confirmation (replace
 * the current source, or run alongside it for data split across systems).
 * Turning one off deactivates it. Credentials live on the Connections page — a
 * source that isn't set up there yet links out to it.
 */
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Breadcrumbs } from '../../components/Breadcrumbs.js';
import { useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Banner,
  Button,
  Card,
  Modal,
  Skeleton,
  StatusIndicator,
} from '@servicecompressionllc/sc-design-system';
import type { StatusTone } from '@servicecompressionllc/sc-design-system';
import { AppShell } from '../../components/AppShell.js';
import { Toggle } from '../../components/Toggle.js';
import { useMe } from '../../hooks/useMe.js';
import { queryKeys } from '../../lib/queryKeys.js';
import type {
  TenantIntegrationSummary,
  CapabilitySourceState,
  IntegrationCategory,
  IntegrationDefinition,
  IntegrationKey,
} from '../../lib/types.js';
import { useIntegrationCatalog } from './useIntegrationCatalog.js';
import { useCapabilitySourcesLogic } from './useCapabilitySourcesLogic.js';
import { useIntegrationsListLogic } from './useIntegrationsListLogic.js';
import { IntegrationForm } from './IntegrationForm.js';
import { CAPABILITY_DESC, capabilityLabel, systemDisplay, TestResultBanner } from './shared.js';

export const CapabilityDetailPage = () => {
  const queryClient = useQueryClient();
  const { capability } = useParams<{ capability: string }>();
  const me = useMe();
  const tenantId = me.data?.tenant.id;
  const catalog = useIntegrationCatalog();
  const caps = useCapabilitySourcesLogic(tenantId);
  // The tenant's configured rows, so a source backed by a shared connection can
  // offer the real candidates to choose between.
  const accounts = useIntegrationsListLogic(tenantId);

  const [activateKey, setActivateKey] = useState<IntegrationKey | null>(null);
  const [mode, setMode] = useState<'replace' | 'add'>('replace');
  const [setupKey, setSetupKey] = useState<IntegrationKey | null>(null);
  /** Connection chosen per source key, before it's activated. */
  const [picked, setPicked] = useState<Record<string, string>>({});

  const integrations = catalog.data?.integrations ?? [];
  const defByKey = useMemo(() => new Map(integrations.map((d) => [d.key, d])), [integrations]);
  const rows = useMemo(() => accounts.data?.integrations ?? [], [accounts.data]);

  /**
   * Candidate connections for a source: the tenant's rows of the kind the source
   * needs. `connectionKey` is null for a self-contained source (M-Link holds its
   * own credentials), which yields no options and so no picker.
   */
  const connectionsFor = (source: CapabilitySourceState): TenantIntegrationSummary[] =>
    source.connectionKey === null ? [] : rows.filter((row) => row.key === source.connectionKey);
  const capState = caps.data?.capabilities.find((c) => c.capability === capability);
  const activeCount = capState?.sources.filter((s) => s.active).length ?? 0;

  // "Set up" configures the source's credentials. For a shared-connection source
  // (netsuite_* / custom_api_*) that's the connection catalog entry; a
  // self-contained source is set up as itself.
  const setupDef = useMemo((): IntegrationDefinition | undefined => {
    if (!setupKey) return undefined;
    const src = defByKey.get(setupKey);
    if (!src) return undefined;
    return integrations.find((i) => i.category === 'connection' && i.system === src.system) ?? src;
  }, [setupKey, defByKey, integrations]);

  const onSetupSuccess = (): void => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.capabilities(tenantId ?? '') });
    void queryClient.invalidateQueries({ queryKey: queryKeys.integrations(tenantId ?? '') });
    setSetupKey(null);
  };

  const backLink = <Breadcrumbs trail={[{ label: 'All capabilities', to: '/capabilities' }]} />;

  if (me.isLoading || catalog.isLoading || caps.isLoading) {
    return (
      <AppShell>
        <Skeleton className="mb-4 h-5 w-40" />
        <Skeleton className="mb-6 h-8 w-56" />
        <Skeleton className="h-48 w-full max-w-3xl" />
      </AppShell>
    );
  }

  if (catalog.error || caps.error || !capState) {
    return (
      <AppShell>
        {backLink}
        <Banner variant={catalog.error || caps.error ? 'danger' : 'warning'}>
          <Banner.Body>
            <Banner.Title>
              {catalog.error || caps.error
                ? 'Could not load this capability.'
                : 'Capability not found'}
            </Banner.Title>
            <Banner.Description>
              {(catalog.error ?? caps.error)?.message ?? `No capability matches “${capability}”.`}
            </Banner.Description>
          </Banner.Body>
        </Banner>
      </AppShell>
    );
  }

  const label = capabilityLabel(capState.capability);
  const activateDef = activateKey ? defByKey.get(activateKey) : undefined;
  const cap = capState.capability as IntegrationCategory;
  /** Only some capabilities merge several sources; the rest read exactly one,
   * and the server refuses `add` for them. Offering the choice anyway sent an
   * admin down a path that always failed, so the modal asks only where the
   * answer is real. */
  const canRunAlongside = capState.supportsMultipleSources;
  /** The source being displaced, named in the single-source confirmation so the
   * admin sees what they are swapping away from. */
  const currentSourceName = capState.sources
    .filter((src) => src.active)
    .map((src) => defByKey.get(src.key)?.displayName)
    .find((name): name is string => name !== undefined);

  /**
   * Which connection an admin has picked for a source, before activating. Keyed
   * by source key. Falls back to what the source is already bound to, so
   * re-toggling a configured source doesn't silently move it.
   */
  const chosenConnectionFor = (source: CapabilitySourceState): string | undefined =>
    picked[source.key] ?? source.connectionId ?? undefined;

  // Turning a source on: activate directly when nothing else is active, else
  // open the swap confirmation so the admin chooses replace vs run-alongside.
  const requestActivate = (source: CapabilitySourceState): void => {
    const connectionId = chosenConnectionFor(source);
    if (activeCount === 0) {
      void caps.activate({
        capability: cap,
        sourceKey: source.key,
        mode: 'replace',
        connectionId,
      });
      return;
    }
    setMode('replace');
    setActivateKey(source.key);
  };

  const confirmActivate = (): void => {
    if (!activateKey) return;
    const source = capState.sources.find((src) => src.key === activateKey);
    void caps.activate({
      capability: cap,
      sourceKey: activateKey,
      mode,
      connectionId: source ? chosenConnectionFor(source) : undefined,
    });
    setActivateKey(null);
  };

  return (
    <AppShell>
      {backLink}
      <h2 className="text-ink text-2xl font-bold">{label}</h2>
      <p className="text-ink-muted mb-5 mt-1 max-w-[70ch] text-sm">
        {CAPABILITY_DESC[capState.capability] ??
          'Choose which connected source serves this capability.'}
        {activeCount > 1
          ? ' This capability queries every active source and merges the results.'
          : ' Turning a source on stays live until you swap it; the previous source keeps its config for rollback.'}
      </p>

      {capability === 'notifications' &&
        me.data &&
        hasPermission(me.data.user, PERMS.notifications.manage) && (
          <p className="mb-5 text-sm">
            <Link to="/settings/notification-defaults" className="text-primary underline">
              Manage organization notification defaults
            </Link>
          </p>
        )}
      <TestResultBanner
        testResult={
          caps.testResult
            ? {
                label:
                  systemDisplay(
                    integrations.find((i) => i.key === caps.testResult?.key)?.system ?? '',
                  ) || caps.testResult.key,
                result: caps.testResult.result,
              }
            : null
        }
      />

      <h3 className="text-ink-subtle mb-2 text-xs font-semibold uppercase tracking-wide">
        Sources
      </h3>
      <Card padding="none">
        <div className="divide-outline divide-y">
          {[...capState.sources]
            .sort((a, b) =>
              systemDisplay(defByKey.get(a.key)?.system ?? a.key).localeCompare(
                systemDisplay(defByKey.get(b.key)?.system ?? b.key),
              ),
            )
            .map((source) => {
              const def = defByKey.get(source.key);
              if (!def) return null;
              return (
                <SourceRow
                  key={source.key}
                  def={def}
                  source={source}
                  busy={
                    caps.activatingKey === source.key ||
                    caps.deactivatingKey === source.key ||
                    caps.testingKey === source.key
                  }
                  onToggle={() =>
                    source.active ? void caps.deactivate(cap, source.key) : requestActivate(source)
                  }
                  onTest={
                    capability === 'notifications' ? undefined : () => void caps.test(source.key)
                  }
                  onSetUp={() => setSetupKey(source.key)}
                  connectionOptions={connectionsFor(source)}
                  chosenConnectionId={chosenConnectionFor(source)}
                  onPickConnection={(id) => setPicked((prev) => ({ ...prev, [source.key]: id }))}
                />
              );
            })}
        </div>
      </Card>

      {setupKey !== null && setupDef && tenantId && (
        <Modal open onOpenChange={(open) => !open && setSetupKey(null)}>
          <Modal.Header>
            <Modal.Title>Connect {setupDef.displayName}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-ink-muted mb-4 text-sm">{setupDef.description}</p>
            <IntegrationForm
              tenantId={tenantId}
              definition={setupDef}
              mode="create"
              onCancel={() => setSetupKey(null)}
              onSuccess={onSetupSuccess}
            />
          </Modal.Body>
        </Modal>
      )}

      {activateKey !== null && (
        <Modal open onOpenChange={(open) => !open && setActivateKey(null)}>
          <Modal.Header>
            <Modal.Title>
              Activate {activateDef?.displayName} for {label}?
            </Modal.Title>
          </Modal.Header>
          <Modal.Description>
            {canRunAlongside
              ? 'This capability already has an active source. Choose how this one comes in.'
              : 'This capability reads a single source, so activating this one replaces what is serving it now.'}
          </Modal.Description>
          <Modal.Body>
            {canRunAlongside ? (
              <>
                <label className="border-outline mb-2 flex cursor-pointer items-start gap-2.5 rounded-lg border p-3">
                  <input
                    type="radio"
                    name="activate-mode"
                    checked={mode === 'replace'}
                    onChange={() => setMode('replace')}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="text-ink block text-sm font-semibold">
                      Replace the current source
                    </span>
                    <span className="text-ink-muted block text-[13px]">
                      This becomes the only active source. The current one stays configured for
                      rollback.
                    </span>
                  </span>
                </label>
                <label className="border-outline flex cursor-pointer items-start gap-2.5 rounded-lg border p-3">
                  <input
                    type="radio"
                    name="activate-mode"
                    checked={mode === 'add'}
                    onChange={() => setMode('add')}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="text-ink block text-sm font-semibold">
                      Run alongside the current source
                    </span>
                    <span className="text-ink-muted block text-[13px]">
                      For data split across systems. The agent queries both and merges results.
                    </span>
                  </span>
                </label>
              </>
            ) : (
              <p className="text-ink-muted text-sm">
                {currentSourceName === undefined
                  ? 'The source serving it now stays configured, so you can switch back at any time.'
                  : `${currentSourceName} stays configured, so you can switch back at any time.`}
              </p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setActivateKey(null)}>
              Cancel
            </Button>
            <Button onClick={confirmActivate}>Activate</Button>
          </Modal.Footer>
        </Modal>
      )}
    </AppShell>
  );
};

const SourceRow = ({
  def,
  source,
  busy,
  onToggle,
  onTest,
  onSetUp,
  connectionOptions,
  chosenConnectionId,
  onPickConnection,
}: {
  def: IntegrationDefinition;
  source: CapabilitySourceState;
  busy: boolean;
  onToggle: () => void;
  onTest?: () => void;
  onSetUp: () => void;
  /** Candidate connections for this source. Empty ⇒ nothing to choose. */
  connectionOptions: TenantIntegrationSummary[];
  chosenConnectionId: string | undefined;
  onPickConnection: (connectionId: string) => void;
}) => {
  const status: { tone: StatusTone; label: string } = source.active
    ? { tone: 'success', label: 'Active' }
    : source.configured
      ? { tone: 'neutral', label: 'Off' }
      : { tone: 'neutral', label: 'Not connected' };

  return (
    <div className="flex items-start justify-between gap-4 px-5 py-4">
      <div className="min-w-0">
        <div className="flex items-center gap-2.5">
          <span className="text-ink font-medium">{def.displayName}</span>
          <Badge variant="neutral">{systemDisplay(def.system)}</Badge>
          <StatusIndicator tone={status.tone}>{status.label}</StatusIndicator>
        </div>
        <p className="text-ink-muted mt-0.5 text-[13px]">{def.description}</p>
        {/*
          Shown only when there's a real choice. With two Custom API connections
          the source key alone doesn't say which serves this capability, and the
          wrong one returns nothing while reporting healthy — so the choice has to
          be visible at the moment of making it.
        */}
        {connectionOptions.length > 1 && (
          <label className="text-ink-muted mt-2 flex flex-wrap items-center gap-2 text-[13px]">
            Reads from
            <select
              value={chosenConnectionId ?? ''}
              onChange={(e) => onPickConnection(e.target.value)}
              disabled={busy}
              className="border-outline-strong bg-surface text-ink rounded border px-2 py-1 text-[13px]"
            >
              <option value="" disabled>
                Choose a connection…
              </option>
              {connectionOptions.map((conn) => (
                <option key={conn.id} value={conn.id}>
                  {conn.name || conn.slug}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      <div className="flex flex-shrink-0 items-center gap-3">
        {source.configured ? (
          <>
            {onTest && (
              <Button size="sm" variant="secondary" onClick={onTest} disabled={busy}>
                Test
              </Button>
            )}
            <Toggle
              checked={source.active}
              onChange={onToggle}
              disabled={busy}
              label={`${source.active ? 'Deactivate' : 'Activate'} ${def.displayName} for this capability`}
            />
          </>
        ) : (
          <Button size="sm" onClick={onSetUp}>
            Set up
          </Button>
        )}
      </div>
    </div>
  );
};
